import api from './api';

// ---------------------------------------------------------------------------
// UI metadata keyed by service code.
// Add a new entry here whenever a new service code appears in the backend.
// ---------------------------------------------------------------------------
const SERVICE_META = {
  pharmacy: {
    description: 'Order medicines & health products delivered to your doorstep.',
    accent: '#4f46e5',
    bg: '#eef2ff',
    route: '/services/pharmacy',
    cta: 'Order Medicines',
  },
  'online-consultation': {
    description: 'Consult certified doctors via video or chat, anytime.',
    accent: '#0891b2',
    bg: '#ecfeff',
    route: '/services/online-consultation',
    cta: 'Consult Now',
  },
  'lab-tests': {
    description: 'Book diagnostic tests with home sample collection.',
    accent: '#7c3aed',
    bg: '#f5f3ff',
    route: '/services/lab-tests',
    cta: 'Book Lab Test',
  },
  'health-records': {
    description: 'Store, manage and share your medical records securely.',
    accent: '#059669',
    bg: '#ecfdf5',
    route: '/services/health-records',
    cta: 'View Records',
  },
  ambulance: {
    description: 'Request an emergency ambulance with real-time tracking.',
    accent: '#dc2626',
    bg: '#fef2f2',
    route: '/services/ambulance',
    cta: 'Book Ambulance',
  },
  'mental-wellness': {
    description: 'Therapy, counselling and mindfulness — at your pace.',
    accent: '#d97706',
    bg: '#fffbeb',
    route: '/services/mental-wellness',
    cta: 'Explore Support',
  },
  vaccination: {
    description: 'Book vaccination slots for you and your family.',
    accent: '#2563eb',
    bg: '#eff6ff',
    route: '/services/vaccination',
    cta: 'Book Vaccination',
  },
  // Add future service codes here as the backend exposes them
};

// Services shown as "coming soon" tiles even when not returned by the backend.
// Remove an entry from here once the backend starts returning it.
const COMING_SOON = [
  {
    id: 'diet-nutrition',
    title: 'Diet & Nutrition',
    description: 'Personalised meal plans from certified nutritionists.',
    icon: '🥗',
    accent: '#16a34a',
    bg: '#f0fdf4',
    available: false,
    route: null,
  },
];

/**
 * Fetches services for the given tenant ID and merges with local UI metadata.
 *
 * @param {string} tenantId  - The X-Tenant-ID header value.
 * @returns {Promise<Array>} - Merged, display-ready service objects.
 */
export const getServices = async (tenantId) => {
  if (!tenantId) {
    console.warn('[servicesApi] No tenantId provided — returning coming-soon list only.');
    return COMING_SOON;
  }

  const { data: raw } = await api.get('/services'); // axios instance handles baseURL + auth headers

  // Build a Set of codes returned by the backend for O(1) lookup
  const backendCodes = new Set(raw.map((s) => s.code));

  // Merge backend list with local UI metadata
  const live = raw
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((s) => {
      const meta = SERVICE_META[s.code] ?? {
        description: `${s.displayName} — coming soon.`,
        accent: '#6b7280',
        bg: '#f9fafb',
        route: null,
      };

      return {
        id: s.code,
        title: s.displayName,
        icon: s.iconUrl,           // emoji string from backend e.g. "💊"
        description: meta.description,
        accent: meta.accent,
        bg: meta.bg,
        route: meta.route,
        cta: meta.cta,
        available: true,           // present in backend response → enabled for tenant
      };
    });

  // Append coming-soon tiles for services not yet in the backend
  const comingSoon = COMING_SOON.filter((s) => !backendCodes.has(s.id));

  return [...live, ...comingSoon];
};